import {type FieldPath, type FieldValues, type PathValue, useController} from "react-hook-form";
import {type TypeaheadSelectProps, TypeaheadSelect} from "app/employee/incdoc/supplier/TypeaheadSelect";
import {hofForwardRef, type MaybeNil, noop, type RhfProps, type RhfRef} from "app/utils/types";
import {type ForwardedRef, type RefCallback, useCallback, useImperativeHandle, useRef} from "react";
import {assignRef, defaultScrollIntoView, isEqual} from "app/utils/stdlib";

const reportValidity = () => true;

export const RhfTypeaheadSelect = hofForwardRef(
  <
    TFieldValues extends FieldValues,
    TName extends FieldPath<TFieldValues>,
    Value extends PathValue<TFieldValues, TName>,
    Option
  >(
    {
      rhf,
      disabled,
      optionMapper,
      ...rest
    }: Omit<TypeaheadSelectProps<Option, Value>, "onSelect" | "value" | "isEqual"> &
      RhfProps<TFieldValues, TName> & {
        optionMapper: (o: MaybeNil<Option>) => MaybeNil<Value>;
      },
    ref: ForwardedRef<HTMLIonItemElement>
  ) => {
    const {
      field: {onChange, value, disabled: rhfDisabled, ref: fieldRef},
      formState: {isSubmitting},
      fieldState: {error}
    } = useController(rhf);

    const rhfRef = useRef<RhfRef | null>(null);
    const inputRef: RefCallback<HTMLIonItemElement> = (element) => {
      assignRef(ref, element);
      rhfRef.current = {
        focus: () => element?.scrollIntoView(defaultScrollIntoView),
        select: noop,
        setCustomValidity: noop,
        reportValidity
      };
    };
    useImperativeHandle(fieldRef, () => rhfRef.current);

    const isEqualOptionToValue = useCallback(
      (option: Option, value: Value) => {
        return isEqual(optionMapper(option), value);
      },
      [optionMapper]
    );

    return (
      <TypeaheadSelect
        isEqual={isEqualOptionToValue}
        ref={inputRef}
        {...rest}
        disabled={isSubmitting || disabled || rhfDisabled}
        errorMessage={error?.message}
        onSelect={(option) => onChange(optionMapper(option))}
        value={value}
      />
    );
  }
);
